var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'whatwg-fetch';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Switch, Route, } from 'react-router-dom';
import { Layer, Source, Marker } from 'react-map-gl/maplibre';
import { useImmer } from "use-immer";
import { Container, Tooltip, Whisper } from 'rsuite';
// Local Components
import ResourceMapUI from './resource-map-components/ResourceMapUI';
import { clusterCountLayer, clusterLayer, cityFill, cityHover, cityLine, countyFill, countyHover, countyLine, stateFill, stateLine, unclusteredPointLayer, allPointLayer, } from './resource-map-components/resourceMapStyles';
import BaseMap from './shared-map-components/BaseMap';
import { ResourceModal } from './resource-map-components/ResourceModal';
import { ModalActionButtons } from './resource-map-components/ModalActionButtons';
import { SelectedResourceContent } from './resource-map-components/SelectedResourceContent';
import bbox from '@turf/bbox';
const defaultGlobalConfig = {
    baseRoute: 'map',
    apiRoute: '/api/pages/spending_map/getRequests',
    pageTitle: '',
    pageBlurb: '',
    mapLocationName: '',
    resourceLabel: 'event',
    countySuffixLabel: ' County',
    stateAbbr: 'CA',
    useFiscalYear: false,
    defaultFilter: {
        featured: false,
        initial: false,
        county: '',
        city: '',
        perPage: 50,
        page: 1,
        year: '',
        benefits: '',
        category: '',
        agency: '',
        recipient: '',
        fundingStream: '',
        amount: '',
        query: '',
        queryCol: '',
        orderBy: '',
        sortDir: '',
    },
};
const defaultGlobalState = {
    loading: false,
    updating: false,
    filteredSpendingRequests: [],
    map: null,
    clusters: [],
    selected: [],
    modalResource: null,
    prevModalResource: null,
    nextModalResource: null,
    fundingStream: null,
    dateScope: {
        start: undefined,
        end: undefined,
    },
    types: [],
    zipCode: null,
    zipCoordinates: null,
    zipRadius: 50,
    zipRadiusPolygon: null,
    totalRows: 0,
    currentFilter: defaultGlobalConfig.defaultFilter || {},
};
const defaultListConfig = {
    showFilterTools: true,
    showFilterString: true,
    showSortBy: true,
    showFilterByCounty: false,
    countyFilterPreposition: 'in',
    distinctColumns: [],
    distinctColValues: {},
    initialDistinctColValues: {},
    filters: [],
    filterYearPrefixLabel: '',
    agencyPrefix: 'from the ',
    fundingStreamPrefix: 'and funded by ',
    yearPrefix: 'during ',
};
const defaultMapConfig = {
    allowZoom: true,
    allowFilterCounty: false,
    allowCountyClick: true,
    clusterMaxZoom: 15,
    clusterRadius: 15,
    showClusters: true,
    showClustersZoomedOut: false,
    showPinsZoomedOut: false,
    clusters: ['default'],
    clusterBy: 'eventType',
    showCountyTooltips: true,
    showCityTooltips: true,
    showMapTiles: true,
    showFilterReset: false,
    mapTiles: 'rastertiles/voyager',
    margin: 10,
    minZoom: 4,
    maxZoom: 18,
    sourceOrder: ['counties'],
    interactiveLayers: ['countyFills'],
    vectorStyle: '',
};
const defaultModalConfig = {
    showShare: false,
};
const ResourceMap = ({ config }) => {
    var _a, _b;
    const mapRef = useRef();
    const [globalConfig] = useImmer(Object.assign(Object.assign({}, defaultGlobalConfig), config.global));
    // Make sure config.selected is in array format even if single uid string
    const selectedArray = Array.isArray(config.selected) ? config.selected : (typeof config.selected == 'string') ? [config.selected] : [];
    const [globalState, setGlobalState] = useImmer(Object.assign(Object.assign({}, defaultGlobalState), { currentFilter: Object.assign(Object.assign({}, defaultGlobalConfig.defaultFilter), globalConfig.defaultFilter), selected: selectedArray }));
    const [listConfig, setListConfig] = useImmer(Object.assign(Object.assign({}, defaultListConfig), config.list));
    const [mapConfig] = useImmer(Object.assign(Object.assign({}, defaultMapConfig), config.map));
    const [modalConfig] = useImmer(Object.assign(Object.assign({}, defaultModalConfig), config.modal));
    const [modalState, setModalState] = useImmer({
        modalResource: (selectedArray === null || selectedArray === void 0 ? void 0 : selectedArray[0]) || null,
        prevModalResource: null,
        nextModalResource: (selectedArray === null || selectedArray === void 0 ? void 0 : selectedArray[1]) || null,
    });
    const [dataByUid, setDataByUid] = useImmer(config.spendingRequestStore || {});
    const [currentList, setCurrentList] = useImmer(selectedArray || []);
    const [modalList, setModalList] = useImmer(selectedArray || []);
    const [markers, setMarkers] = useImmer({
        'type': 'FeatureCollection',
        'features': []
    });
    const updateModalResource = (itemId) => {
        if (!itemId)
            itemId = modalList[0] || null;
        const filteredIndex = modalList.findIndex(filtereditemId => (filtereditemId === itemId));
        const prevModalResource = modalList[filteredIndex - 1] || null;
        const nextModalResource = modalList[filteredIndex + 1] || null;
        setModalState(draft => {
            draft.modalResource = itemId;
            draft.prevModalResource = prevModalResource;
            draft.nextModalResource = nextModalResource;
        });
    };
    useEffect(() => {
        if (modalList.length) {
            const missingData = modalList.filter(uid => Boolean(!dataByUid[uid]));
            if (missingData.length)
                fetchUids(missingData);
            modalState.modalResource ? updateModalResource(modalState.modalResource) : updateModalResource(modalList[0]);
        }
        else {
            updateModalResource();
        }
    }, [modalList]);
    const onModalClose = () => {
        setModalList([]);
    };
    // GeoJSON data for shape layers
    const [mapStateSource, setStateShape] = useState(undefined);
    const [mapDistrictSource, setDistrictShape] = useState(undefined);
    const [mapCountiesSource, setCountiesShape] = useState(undefined);
    const [mapCitiesSource, setCitiesShape] = useState(undefined);
    const mapSources = {
        state: mapStateSource,
        district: mapDistrictSource,
        counties: mapCountiesSource,
        cities: mapCitiesSource,
    };
    const fetchRegions = () => {
        const setters = {
            state: setStateShape,
            district: setDistrictShape,
            counties: setCountiesShape,
            cities: setCitiesShape,
        };
        const sources = mapConfig.sourceData || {};
        Object.entries(sources).forEach(([source, url]) => {
            fetch(url).then((res) => {
                res.json().then(data => {
                    if (typeof setters[source] == 'function') {
                        setters[source](data);
                    }
                });
            });
        });
    };
    useEffect(() => {
        if (!(mapCitiesSource === null || mapCitiesSource === void 0 ? void 0 : mapCitiesSource.features))
            return;
        let cityFeatures = mapCitiesSource.features.map(feature => feature.properties.NAME);
        cityFeatures = cityFeatures.sort();
        setListConfig(draft => {
            draft.distinctColValues.city = cityFeatures;
        });
    }, [mapCitiesSource]);
    useEffect(() => {
        if (!(mapCountiesSource === null || mapCountiesSource === void 0 ? void 0 : mapCountiesSource.features))
            return;
        let countyFeatures = mapCountiesSource.features.map(feature => feature.properties.NAME);
        countyFeatures = countyFeatures.sort();
        setListConfig(draft => {
            draft.distinctColValues.county = countyFeatures;
        });
    }, [mapCountiesSource]);
    const getFeatureBounds = (collection) => {
        var _a;
        const newBounds = {};
        (_a = collection === null || collection === void 0 ? void 0 : collection.features) === null || _a === void 0 ? void 0 : _a.forEach(feature => {
            const bounds = bbox(feature);
            const name = feature.properties.NAME;
            if (name)
                newBounds[name] = bounds;
        });
        return newBounds;
    };
    const [allCountyBounds, setCountyBounds] = useState({});
    useEffect(() => {
        if (!mapCountiesSource)
            return;
        const newBounds = getFeatureBounds(mapCountiesSource);
        setCountyBounds(Object.assign(Object.assign({}, allCountyBounds), newBounds));
    }, [mapCountiesSource]);
    const [allCityBounds, setCityBounds] = useState({});
    useEffect(() => {
        if (!mapCitiesSource)
            return;
        const newBounds = getFeatureBounds(mapCitiesSource);
        setCityBounds(Object.assign(Object.assign({}, allCityBounds), newBounds));
    }, [mapCitiesSource]);
    const fetchUids = (uidList) => {
        const params = Object.assign({ uids: uidList }, globalState.currentFilter);
        fetch(globalConfig.apiRoute + '?' + new URLSearchParams(params)).then((res) => {
            res.json().then(data => {
                const { spendingRequestStore, } = data;
                setDataByUid(Object.assign(Object.assign({}, dataByUid), spendingRequestStore));
            });
        });
    };
    // GeoJSON data for symbol layers
    const fetchResources = () => {
        const params = Object.assign({ geoJSONMarkers: true, distinctColumns: listConfig.distinctColumns, useFiscalYear: globalConfig.useFiscalYear }, globalState.currentFilter);
        fetch(globalConfig.apiRoute + '?' + new URLSearchParams(params)).then((res) => {
            res.json().then(data => {
                var _a;
                const { mapPins, filteredSpendingRequests, distinctColValues, spendingRequestStore, } = data;
                setMarkers(mapPins);
                setDataByUid(Object.assign(Object.assign({}, dataByUid), spendingRequestStore));
                if (globalState.currentFilter.page > 1) {
                    setCurrentList([...currentList, ...filteredSpendingRequests]);
                }
                else {
                    setCurrentList(filteredSpendingRequests);
                }
                setGlobalState(draft => {
                    draft.totalRows = (filteredSpendingRequests === null || filteredSpendingRequests === void 0 ? void 0 : filteredSpendingRequests.length) ? spendingRequestStore[filteredSpendingRequests[0]]['TotalRows'] : 0;
                });
                if (!distinctColValues || !Object.keys(distinctColValues).length)
                    return;
                const initialDistinctColValues = Object.assign({}, listConfig.initialDistinctColValues);
                if ((_a = listConfig.filters) === null || _a === void 0 ? void 0 : _a.length) {
                    listConfig.filters.forEach(filterRow => {
                        filterRow.forEach(filter => {
                            var _a;
                            let scoped = true;
                            if (typeof filter.scoped == 'boolean' && filter.scoped === false)
                                scoped = false;
                            if (scoped)
                                return;
                            const hasInitial = Boolean((_a = initialDistinctColValues === null || initialDistinctColValues === void 0 ? void 0 : initialDistinctColValues[filter.column]) === null || _a === void 0 ? void 0 : _a.length);
                            // If the filter field doesn't scope and initial isn't set yet
                            if (!scoped && !hasInitial) {
                                initialDistinctColValues[filter.column] = distinctColValues[filter.column];
                            }
                            // If the filter field doesn't scope with other filters then delete the new DB values
                            if (!scoped) {
                                if (hasInitial) {
                                    distinctColValues[filter.column] = initialDistinctColValues === null || initialDistinctColValues === void 0 ? void 0 : initialDistinctColValues[filter.column];
                                }
                            }
                        });
                    });
                }
                if (!distinctColValues)
                    return;
                setListConfig(draft => {
                    if (Object.keys(initialDistinctColValues).length)
                        draft.initialDistinctColValues = initialDistinctColValues;
                    draft.distinctColValues = Object.assign(Object.assign({}, draft.distinctColValues), distinctColValues);
                });
            });
        });
    };
    useEffect(() => {
        fetchResources();
    }, [globalState.currentFilter]);
    const handleMapLoad = ({ target: _map }) => {
        fetchRegions();
        fetchResources();
    };
    const [hoveredCounty, setHoveredCounty] = useState('');
    const [hoveredCity, setHoveredCity] = useState('');
    const [mapCursor, setMapCursor] = useState('grab');
    const handleMapHover = (event) => {
        var _a, _b, _c, _d;
        const hoveredRegionFragment = event.features && event.features[0];
        const hoveredCountyName = ((_a = hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.layer) === null || _a === void 0 ? void 0 : _a.source) == 'counties' ? (hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties.name) || (hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties.NAME) || (hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties.COUNTY_NAM) || '' : '';
        const hoveredCityName = ((_b = hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.layer) === null || _b === void 0 ? void 0 : _b.source) == 'cities' ? (hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties.name) || (hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties.NAME) || '' : '';
        const hoveredIsCluster = Boolean((_c = hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties) === null || _c === void 0 ? void 0 : _c.cluster);
        const hoveredUid = ((_d = hoveredRegionFragment === null || hoveredRegionFragment === void 0 ? void 0 : hoveredRegionFragment.properties) === null || _d === void 0 ? void 0 : _d['uid']) || '';
        if (hoveredCountyName) {
            setHoveredCounty(hoveredCountyName);
            setHoveredCity('');
        }
        else if (hoveredCityName) {
            setHoveredCounty('');
            setHoveredCity(hoveredCityName);
        }
        else {
            setHoveredCounty('');
            setHoveredCity('');
        }
        if (hoveredUid) {
            selectResources([hoveredUid]);
        }
        else {
            selectResources([]);
        }
        setMapCursor((hoveredUid || hoveredIsCluster) ? 'pointer' : 'grab');
    };
    const handleMapMouseOut = (event) => {
        setHoveredCounty('');
        setHoveredCity('');
        selectResources([]);
    };
    const hoveredCountyFilter = useMemo(() => ['==', 'NAME', hoveredCounty], [hoveredCounty]);
    const hoveredCityFilter = useMemo(() => ['==', 'NAME', hoveredCity], [hoveredCity]);
    const [currentBounds, setCurrentBounds] = useState();
    const [mapBoundsReset, resetMapBounds] = useState(0);
    const resetMap = () => {
        setGlobalState(draft => {
            draft.currentFilter = globalConfig.defaultFilter;
        });
        resetMapBounds(mapBoundsReset + 1);
    };
    const resetFilters = () => {
        setGlobalState(draft => {
            draft.currentFilter = globalConfig.defaultFilter;
        });
    };
    const nextPage = () => {
        setGlobalState(draft => {
            draft.currentFilter.page = globalState.currentFilter.page + 1;
        });
    };
    const handleMapClick = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const clickedRegionFragment = event.features && event.features[0];
        const clickedCountyName = ((_a = clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.layer) === null || _a === void 0 ? void 0 : _a.source) == 'counties' ? (clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties.name) || (clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties.NAME) || '' : '';
        const clickedCityName = ((_b = clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.layer) === null || _b === void 0 ? void 0 : _b.source) == 'cities' ? (clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties.name) || (clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties.NAME) || '' : '';
        const clickedIsCluster = Boolean((_c = clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties) === null || _c === void 0 ? void 0 : _c.cluster);
        const clickedUid = ((_d = clickedRegionFragment === null || clickedRegionFragment === void 0 ? void 0 : clickedRegionFragment.properties) === null || _d === void 0 ? void 0 : _d['uid']) || '';
        if (clickedCountyName) {
            setGlobalState(draft => {
                draft.currentFilter.county = clickedCountyName;
                draft.currentFilter.city = '';
            });
            setHoveredCounty('');
            setHoveredCity('');
        }
        else if (clickedCityName) {
            setGlobalState(draft => {
                draft.currentFilter.county = '';
                draft.currentFilter.city = clickedCityName;
            });
            setHoveredCounty('');
            setHoveredCity('');
        }
        else if (clickedIsCluster) {
            if (clickedRegionFragment && ((_e = clickedRegionFragment.geometry) === null || _e === void 0 ? void 0 : _e.type) == 'Point') {
                const map = mapRef.current;
                if (map) {
                    const features = map.queryRenderedFeatures(event.point, {
                        layers: ['clusters']
                    });
                    const clusterSource = map.getSource('clusteredMarkers');
                    setModalList(draft => {
                        draft = [];
                    });
                    const recursiveResourceFinder = (feature) => __awaiter(void 0, void 0, void 0, function* () {
                        var _a, _b;
                        const found = [];
                        if ((_a = feature === null || feature === void 0 ? void 0 : feature.properties) === null || _a === void 0 ? void 0 : _a.uid) {
                            found.push(feature.properties.uid);
                        }
                        else if ((_b = feature === null || feature === void 0 ? void 0 : feature.properties) === null || _b === void 0 ? void 0 : _b.cluster) {
                            const nestedFeatures = yield clusterSource.getClusterLeaves(feature.properties.cluster_id, Infinity, 0);
                            nestedFeatures.forEach(feature => { var _a; return found.push((_a = feature === null || feature === void 0 ? void 0 : feature.properties) === null || _a === void 0 ? void 0 : _a.uid); });
                        }
                        return found;
                    });
                    recursiveResourceFinder(features[0]).then(foundResources => {
                        setModalList(draft => {
                            draft.push(...foundResources);
                        });
                    });
                }
            }
        }
        else if (clickedUid) {
            setModalList(draft => {
                draft.push(clickedUid);
            });
        }
    });
    const countyVisibilityFilter = useMemo(() => {
        const mapFocusedOnNonCounty = Boolean(globalState.currentFilter.city);
        if (mapFocusedOnNonCounty) {
            return (['has', 'null']);
        }
        else {
            return (['!=', 'NAME', globalState.currentFilter.county]);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    const cityVisibilityFilter = useMemo(() => {
        return (['!=', 'NAME', globalState.currentFilter.city]);
    }, [globalState.currentFilter.city]);
    const showClustersFilter = useMemo(() => {
        let showClusters = false;
        const focusedOnCounty = Boolean(globalState.currentFilter.county && !['all', 'statewide'].includes(globalState.currentFilter.county));
        const focusedOnCity = Boolean(globalState.currentFilter.city && !['all', 'statewide'].includes(globalState.currentFilter.city));
        const focusedOnRegion = Boolean(focusedOnCounty || focusedOnCity);
        if (focusedOnRegion && mapConfig.showClusters)
            showClusters = true;
        if (mapConfig.showClustersZoomedOut && mapConfig.showClusters)
            showClusters = true;
        if (showClusters) {
            return (['has', 'point_count']);
        }
        else {
            return (['has', 'null']);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    const showMarkersFilter = useMemo(() => {
        const focusedOnCounty = Boolean(globalState.currentFilter.county && !['all', 'statewide'].includes(globalState.currentFilter.county));
        const focusedOnCity = Boolean(globalState.currentFilter.city && !['all', 'statewide'].includes(globalState.currentFilter.city));
        const focusedOnRegion = Boolean(focusedOnCounty || focusedOnCity);
        if (focusedOnRegion) {
            return (['!', ['has', 'point_count']]);
        }
        else {
            return (['==', 'null', 'null']);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    const showAllMarkersFilter = useMemo(() => {
        const focusedOnCounty = Boolean(globalState.currentFilter.county && !['all', 'statewide'].includes(globalState.currentFilter.county));
        const focusedOnCity = Boolean(globalState.currentFilter.city && !['all', 'statewide'].includes(globalState.currentFilter.city));
        const focusedOnRegion = Boolean(focusedOnCounty || focusedOnCity);
        if (focusedOnRegion) {
            return (['has', 'point_count']);
        }
        else {
            return (['!', ['has', 'point_count']]);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    useEffect(() => {
        if (globalState.currentFilter.county || globalState.currentFilter.city) {
            const countyBounds = allCountyBounds[globalState.currentFilter.county];
            const cityBounds = allCityBounds[globalState.currentFilter.city];
            if (countyBounds) {
                setCurrentBounds(countyBounds);
            }
            else if (cityBounds) {
                setCurrentBounds(cityBounds);
            }
        }
        else {
            setCurrentBounds(undefined);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    const selectResources = (items) => {
        setGlobalState(draft => {
            draft.selected = items || [];
        });
    };
    const updateFilter = (newFilter) => {
        setGlobalState(draft => {
            draft.currentFilter = Object.assign(Object.assign({}, draft.currentFilter), newFilter);
        });
    };
    const [interactiveLayers, setInteractiveLayers] = useState(mapConfig.interactiveLayers);
    useEffect(() => {
        if (globalState.currentFilter.county || globalState.currentFilter.city) {
            const layers = [...mapConfig.interactiveLayers, 'clusters', 'unclusteredPoints'];
            setInteractiveLayers(layers);
        }
        else {
            const layers = [...mapConfig.interactiveLayers];
            setInteractiveLayers(layers);
        }
    }, [globalState.currentFilter.county, globalState.currentFilter.city]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Whisper, { open: Boolean(hoveredCounty || hoveredCity), placement: 'top', enterable: false, followCursor: true, preventOverflow: true, speaker: React.createElement(Tooltip, null, (hoveredCounty && `${hoveredCounty} County`)
                || (hoveredCity && `${hoveredCity}`)
                || '') },
            React.createElement(Container, null,
                React.createElement(BaseMap, { id: "resourceMap", cursor: mapCursor, mapConfig: mapConfig, globalConfig: globalConfig, currentBounds: currentBounds, resetBounds: mapBoundsReset, onLoad: handleMapLoad, onHover: handleMapHover, onMouseOut: handleMapMouseOut, onClick: handleMapClick, interactiveLayerIds: interactiveLayers, minZoom: mapConfig.minZoom, maxZoom: mapConfig.maxZoom, layers: React.createElement(React.Fragment, null,
                        mapConfig.sourceOrder.map(source => {
                            if (!mapSources[source])
                                return;
                            switch (source) {
                                case 'state': return (React.createElement(Source, { key: 'state', id: 'state', type: 'geojson', data: mapStateSource },
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border' }, stateFill)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border' }, stateLine))));
                                case 'district': return (React.createElement(Source, { key: 'district', id: 'district', type: 'geojson', data: mapDistrictSource },
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border', filter: countyVisibilityFilter }, countyFill)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border', filter: hoveredCountyFilter }, countyHover)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border' }, countyLine))));
                                case 'counties': return (React.createElement(Source, { key: 'counties', id: 'counties', type: 'geojson', data: mapCountiesSource },
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border', filter: countyVisibilityFilter }, countyFill)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border', filter: hoveredCountyFilter }, countyHover)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Country border' }, countyLine))));
                                case 'cities': return (React.createElement(Source, { key: 'cities', id: 'cities', type: 'geojson', data: mapCitiesSource },
                                    React.createElement(Layer, Object.assign({ beforeId: 'Other border', filter: cityVisibilityFilter }, cityFill)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Other border', filter: hoveredCityFilter }, cityHover)),
                                    React.createElement(Layer, Object.assign({ beforeId: 'Other border' }, cityLine))));
                                default:
                                    return (React.createElement(React.Fragment, null));
                            }
                        }),
                        markers && (React.createElement(Source, { id: 'clusteredMarkers', type: 'geojson', data: markers, cluster: mapConfig.showClusters, clusterMaxZoom: mapConfig.clusterMaxZoom, clusterRadius: 0.001 },
                            React.createElement(Layer, Object.assign({ filter: showClustersFilter }, clusterLayer)),
                            React.createElement(Layer, Object.assign({ filter: showClustersFilter }, clusterCountLayer)),
                            React.createElement(Layer, Object.assign({ filter: showMarkersFilter }, unclusteredPointLayer)))),
                        Boolean(markers && mapConfig.showPinsZoomedOut && !mapConfig.showClustersZoomedOut) &&
                            React.createElement(Source, { id: 'markers', type: 'geojson', data: markers, cluster: false },
                                React.createElement(Layer, Object.assign({ beforeId: 'Place labels', filter: showAllMarkersFilter }, allPointLayer))),
                        Boolean(markers && ((_a = globalState.selected) === null || _a === void 0 ? void 0 : _a.length)) && ((_b = globalState.selected) === null || _b === void 0 ? void 0 : _b.map((uid) => {
                            var _a, _b;
                            const resource = dataByUid[uid];
                            return Boolean((_a = resource === null || resource === void 0 ? void 0 : resource.addresses) === null || _a === void 0 ? void 0 : _a.length) && ((_b = resource === null || resource === void 0 ? void 0 : resource.addresses) === null || _b === void 0 ? void 0 : _b.map(({ lat, lng }, index) => (React.createElement(Marker, { key: `${uid}--${index}`, longitude: lng, latitude: lat, anchor: 'bottom', style: { pointerEvents: 'none' } },
                                React.createElement("svg", { className: "EventMap__pin", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 27 40" },
                                    React.createElement("path", { className: "EventMap__pinMarker", d: "M25.78 19.44C29.66 10.76 23.84.57 14.35.02 14.07.01 13.78 0 13.5 0c-.29 0-.57.01-.85.02C3.16.57-2.66 10.76 1.22 19.44L13.5 40l12.28-20.56z" }),
                                    React.createElement("circle", { className: "EventMap__pinEye", cx: "13.5", cy: "12.41", r: "6.81" }))))));
                        }))), mapRef: mapRef },
                    React.createElement(ResourceMapUI, { globalConfig: globalConfig, globalState: globalState, listConfig: listConfig, updateFilter: updateFilter, currentList: currentList, selectedResources: globalState.selected, dataByUid: dataByUid, onResetMap: resetMap, onResetFilters: resetFilters, onUpdateModalResource: updateModalResource, onSelectResource: selectResources, nextPage: nextPage })))),
        React.createElement(ResourceModal, { isOpen: Boolean(modalState.modalResource), onClose: onModalClose, actionButtons: React.createElement(ModalActionButtons, { updateModalResource: updateModalResource, currentModalResource: modalState.modalResource, prevModalResource: modalState.prevModalResource, nextModalResource: modalState.nextModalResource, modalList: modalList }) }, (modalState.modalResource && dataByUid[modalState.modalResource]) &&
            React.createElement(SelectedResourceContent, { modalResource: dataByUid[modalState.modalResource], showShare: modalConfig.showShare })),
        React.createElement(Switch, null,
            React.createElement(Route, { path: `/${globalConfig.baseRoute}` }),
            React.createElement(Route, { path: '/*' }))));
};
const mapContainer = window.mapContainer || document.getElementById('resource-map');
ReactDOM.render(React.createElement(BrowserRouter, { basename: `/${window.mapProps.global.baseRoute}` },
    React.createElement(ResourceMap, { config: window.mapProps || {} })), mapContainer);
// Remove app on Sen-N sites with page transitions
if (typeof window.transitions !== 'undefined' && window.transitions.hooks) {
    window.transitions.hooks.leave(function () {
        ReactDOM.unmountComponentAtNode(mapContainer);
    });
}
